import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const NAVY     = '#0D1B2A';
const NAVY_MID = '#132233';
const AMBER    = '#F5A623';
const WHITE    = '#FFFFFF';
const GREEN    = '#1D9E75';
const FONT_BODY = "'DM Sans', sans-serif";

// Every known setting gets a friendly label/description/input type here. A row whose key
// isn't listed still renders (generic label, plain text input) rather than disappearing --
// so a value added directly in the DB is always editable here, even before this map catches
// up.
const SETTING_META = {
  employer_trial_days: {
    labelKey: 'admin.dashboard.settings.employerTrialDaysLabel',
    descriptionKey: 'admin.dashboard.settings.employerTrialDaysDescription',
    type: 'number',
    min: 1,
  },
  founding_employer_promo_cap: {
    labelKey: 'admin.dashboard.settings.foundingPromoCapLabel',
    descriptionKey: 'admin.dashboard.settings.foundingPromoCapDescription',
    type: 'number',
    min: 0,
  },
};

export default function AdminSettings({ supabase }) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [edits, setEdits]       = useState({});     // key -> in-progress input value
  const [saving, setSaving]     = useState({});     // key -> bool
  const [status, setStatus]     = useState({});     // key -> { ok: bool, msg }

  const [invites, setInvites]           = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(true);
  const [foundingEmail, setFoundingEmail]   = useState('');
  const [foundingSubmitting, setFoundingSubmitting] = useState(false);
  const [foundingMsg, setFoundingMsg]       = useState(null); // { ok: bool, text }

  const loadInvites = async () => {
    const { data } = await supabase
      .from('founding_employer_invites')
      .select('id, email, status, created_at, redeemed_at')
      .order('created_at', { ascending: false });
    setInvites(data || []);
    setInvitesLoading(false);
  };

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from('app_settings').select('key, value').order('key', { ascending: true });
      setSettings(data || []);
      setEdits(Object.fromEntries((data || []).map(row => [row.key, row.value])));
      setLoading(false);
    };
    load();
    loadInvites();
  }, [supabase]);

  const foundingCap = parseInt(settings.find(s => s.key === 'founding_employer_promo_cap')?.value, 10) || 0;
  const foundingUsed = invites.filter(i => i.status !== 'revoked').length;
  const foundingRemaining = Math.max(0, foundingCap - foundingUsed);

  const handleCreateFoundingInvite = async () => {
    const email = foundingEmail.trim();
    if (!email) {
      setFoundingMsg({ ok: false, text: t('admin.dashboard.settings.foundingEmailRequiredError') });
      return;
    }
    setFoundingSubmitting(true);
    setFoundingMsg(null);

    const { data: { session } } = await supabase.auth.getSession();
    try {
      const res = await fetch('/api/create-founding-promo-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ email }),
      });
      const result = await res.json();
      if (!res.ok || !result.invite) {
        setFoundingMsg({ ok: false, text: result.error || t('admin.dashboard.settings.errorSaving') });
        setFoundingSubmitting(false);
        return;
      }

      // Best-effort, matching the org-invite pattern -- a failed send shouldn't undo an
      // otherwise-successful, real invite; the DB row (and Stripe Promotion Code) already exist.
      try {
        await fetch('/api/notify-interest', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'founding_employer_promo',
            recipient_email: email,
            trial_days: result.invite.trial_days_granted,
            invite_link: `${window.location.origin}/employer/pricing?founding=${result.invite.invite_code}`,
          }),
        });
      } catch (_) {}

      setInvites(prev => [result.invite, ...prev]);
      setFoundingEmail('');
      setFoundingSubmitting(false);
      setFoundingMsg({ ok: true, text: t('admin.dashboard.settings.foundingInviteSentMsg') });
    } catch (err) {
      setFoundingMsg({ ok: false, text: err.message || t('admin.dashboard.settings.errorSaving') });
      setFoundingSubmitting(false);
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  const statusColor = (s) => (s === 'redeemed' ? GREEN : s === 'pending' ? AMBER : 'rgba(255,255,255,0.4)');

  const handleSave = async (key) => {
    const meta = SETTING_META[key];
    const value = edits[key];

    if (meta?.type === 'number') {
      const n = parseInt(value, 10);
      if (!Number.isInteger(n) || n < (meta.min ?? 0) || String(n) !== String(value).trim()) {
        setStatus(prev => ({ ...prev, [key]: { ok: false, msg: t('admin.dashboard.settings.invalidNumberError') } }));
        return;
      }
    }

    setSaving(prev => ({ ...prev, [key]: true }));
    setStatus(prev => ({ ...prev, [key]: null }));
    const { error } = await supabase.from('app_settings').update({ value: String(value).trim() }).eq('key', key);
    setSaving(prev => ({ ...prev, [key]: false }));

    if (error) {
      setStatus(prev => ({ ...prev, [key]: { ok: false, msg: t('admin.dashboard.settings.errorSaving') } }));
      return;
    }
    setSettings(prev => prev.map(row => (row.key === key ? { ...row, value: String(value).trim() } : row)));
    setStatus(prev => ({ ...prev, [key]: { ok: true, msg: t('admin.dashboard.settings.savedLabel') } }));
    setTimeout(() => setStatus(prev => ({ ...prev, [key]: null })), 2500);
  };

  if (loading) return null;

  return (
    <div style={{ padding: '24px', maxWidth: '640px' }}>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: WHITE, marginBottom: '4px', fontFamily: FONT_BODY }}>
        {t('admin.dashboard.settings.heading')}
      </h2>
      <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)', marginBottom: '24px', fontFamily: FONT_BODY }}>
        {t('admin.dashboard.settings.subheading')}
      </p>

      {settings.map(row => {
        const meta = SETTING_META[row.key];
        const rowStatus = status[row.key];
        return (
          <div
            key={row.key}
            style={{
              backgroundColor: NAVY_MID, borderRadius: '10px', padding: '16px 18px',
              marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '10px',
            }}
          >
            <div>
              <div style={{ fontSize: '0.88rem', fontWeight: 600, color: WHITE, fontFamily: FONT_BODY }}>
                {meta ? t(meta.labelKey) : row.key}
              </div>
              {meta?.descriptionKey && (
                <div style={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.4)', marginTop: '2px', fontFamily: FONT_BODY }}>
                  {t(meta.descriptionKey)}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <input
                type={meta?.type === 'number' ? 'number' : 'text'}
                min={meta?.min}
                value={edits[row.key] ?? ''}
                onChange={e => setEdits(prev => ({ ...prev, [row.key]: e.target.value }))}
                style={{
                  width: '110px', padding: '9px 12px', fontSize: '0.9rem',
                  border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px',
                  color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)',
                  fontFamily: FONT_BODY,
                }}
              />
              <button
                onClick={() => handleSave(row.key)}
                disabled={saving[row.key] || edits[row.key] === row.value}
                style={{
                  background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.82rem',
                  padding: '9px 18px', borderRadius: '8px', fontFamily: FONT_BODY,
                  cursor: saving[row.key] || edits[row.key] === row.value ? 'default' : 'pointer',
                  opacity: saving[row.key] || edits[row.key] === row.value ? 0.5 : 1,
                }}
              >
                {saving[row.key] ? t('admin.dashboard.settings.savingButton') : t('admin.dashboard.settings.saveButton')}
              </button>
              {rowStatus && (
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: rowStatus.ok ? GREEN : '#F87171', fontFamily: FONT_BODY }}>
                  {rowStatus.msg}
                </span>
              )}
            </div>
          </div>
        );
      })}

      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: WHITE, marginTop: '32px', marginBottom: '4px', fontFamily: FONT_BODY }}>
        {t('admin.dashboard.settings.foundingPromoHeading')}
      </h2>
      <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)', marginBottom: '16px', fontFamily: FONT_BODY }}>
        {t('admin.dashboard.settings.foundingPromoSubheading', { remaining: foundingRemaining, cap: foundingCap })}
      </p>

      <div style={{ backgroundColor: NAVY_MID, borderRadius: '10px', padding: '16px 18px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <input
            type="email"
            placeholder={t('admin.dashboard.settings.foundingEmailPlaceholder')}
            value={foundingEmail}
            onChange={e => setFoundingEmail(e.target.value)}
            disabled={foundingRemaining <= 0}
            style={{
              flex: '1 1 220px', padding: '9px 12px', fontSize: '0.9rem',
              border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px',
              color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)',
              fontFamily: FONT_BODY, opacity: foundingRemaining <= 0 ? 0.5 : 1,
            }}
          />
          <button
            onClick={handleCreateFoundingInvite}
            disabled={foundingSubmitting || foundingRemaining <= 0}
            style={{
              background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.82rem',
              padding: '9px 18px', borderRadius: '8px', fontFamily: FONT_BODY, whiteSpace: 'nowrap',
              cursor: foundingSubmitting || foundingRemaining <= 0 ? 'default' : 'pointer',
              opacity: foundingSubmitting || foundingRemaining <= 0 ? 0.5 : 1,
            }}
          >
            {foundingSubmitting ? t('admin.dashboard.settings.foundingSendingButton') : t('admin.dashboard.settings.foundingGenerateButton')}
          </button>
        </div>
        {foundingRemaining <= 0 && (
          <p style={{ fontSize: '0.8rem', color: '#F87171', marginTop: '10px', fontFamily: FONT_BODY }}>
            {t('admin.dashboard.settings.foundingCapReachedMsg')}
          </p>
        )}
        {foundingMsg && (
          <p style={{ fontSize: '0.8rem', fontWeight: 600, color: foundingMsg.ok ? GREEN : '#F87171', marginTop: '10px', fontFamily: FONT_BODY }}>
            {foundingMsg.text}
          </p>
        )}
      </div>

      {!invitesLoading && invites.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {invites.map(inv => (
            <div
              key={inv.id}
              style={{
                backgroundColor: NAVY_MID, borderRadius: '8px', padding: '10px 14px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap',
              }}
            >
              <span style={{ fontSize: '0.84rem', color: WHITE, fontFamily: FONT_BODY }}>{inv.email}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.4)', fontFamily: FONT_BODY }}>{fmtDate(inv.created_at)}</span>
                <span style={{ fontSize: '0.76rem', fontWeight: 700, color: statusColor(inv.status), fontFamily: FONT_BODY, textTransform: 'capitalize' }}>
                  {inv.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
